import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { AmbientBackground } from './components/AmbientBackground';
import { AppFooter } from './components/AppFooter';
import { BrandHeader } from './components/BrandHeader';
import { LiquidTransition, type LiquidTransitionHandle } from './components/LiquidTransition';
import { OperatorPanel } from './components/OperatorPanel';
import { QuestionView } from './components/QuestionView';
import { ResultView } from './components/ResultView';
import { StartView } from './components/StartView';
import { QUESTIONS } from './data/questions';
import { useKioskControls } from './hooks/useKioskControls';
import { usePersistentSettings } from './hooks/usePersistentSettings';
import { usePusherControls } from './hooks/usePusherControls';
import type { SurveyQuestion, SurveySessionState, SurveySettings } from './types/survey';
import { prepareQuestions } from './utils/survey';

gsap.registerPlugin(useGSAP);

type SessionAction =
  | { type: 'START'; questions: SurveyQuestion[] }
  | { type: 'ANSWER'; answerId: number }
  | { type: 'NEXT' }
  | { type: 'RESET' };

const initialSession: SurveySessionState = {
  phase: 'start',
  questions: [],
  index: 0,
  score: 0,
  selectedAnswerId: null,
};

function sessionReducer(
  state: SurveySessionState,
  action: SessionAction,
): SurveySessionState {
  switch (action.type) {
    case 'START':
      return {
        phase: 'question',
        questions: action.questions,
        index: 0,
        score: 0,
        selectedAnswerId: null,
      };

    case 'ANSWER': {
      if (state.phase !== 'question' || state.selectedAnswerId !== null) return state;
      const currentQuestion = state.questions[state.index];
      if (!currentQuestion) return state;

      return {
        ...state,
        selectedAnswerId: action.answerId,
        score: state.score + (action.answerId === currentQuestion.correctAnswer ? 1 : 0),
      };
    }

    case 'NEXT':
      if (state.phase !== 'question') return state;
      if (state.index >= state.questions.length - 1) {
        return { ...state, phase: 'result', selectedAnswerId: null };
      }
      return {
        ...state,
        index: state.index + 1,
        selectedAnswerId: null,
      };

    case 'RESET':
      return initialSession;

    default:
      return state;
  }
}

export default function App() {
  const [session, dispatch] = useReducer(sessionReducer, initialSession);
  const [operatorOpen, setOperatorOpen] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [questionCountdown, setQuestionCountdown] = useState(0);
  const transitionRef = useRef<LiquidTransitionHandle>(null);
  const transitionLockedRef = useRef(false);
  const { settings, updateSettings } = usePersistentSettings(QUESTIONS.length);

  const currentQuestion = session.questions[session.index];

  const transitionTo = useCallback((callback: () => void) => {
    if (transitionLockedRef.current) return;
    transitionLockedRef.current = true;

    const transition = transitionRef.current;
    if (!transition) {
      callback();
      transitionLockedRef.current = false;
      return;
    }

    transition.play(callback, () => {
      transitionLockedRef.current = false;
    });
  }, []);

  const startSession = useCallback(() => {
    const prepared = prepareQuestions(QUESTIONS, settings);
    transitionTo(() => dispatch({ type: 'START', questions: prepared }));
  }, [settings, transitionTo]);

  const resetSession = useCallback(() => {
    setOperatorOpen(false);
    transitionTo(() => dispatch({ type: 'RESET' }));
  }, [transitionTo]);

  const nextQuestion = useCallback(() => {
    transitionTo(() => dispatch({ type: 'NEXT' }));
  }, [transitionTo]);

  const selectByPosition = useCallback((position: number) => {
    if (session.phase !== 'question' || session.selectedAnswerId !== null) return;
    const answer = currentQuestion?.answers[position - 1];
    if (!answer) return;
    dispatch({ type: 'ANSWER', answerId: answer.id });
  }, [currentQuestion, session.phase, session.selectedAnswerId]);

  const applySettings = useCallback((nextSettings: SurveySettings) => {
    updateSettings(nextSettings);
    setOperatorOpen(false);
    transitionTo(() => dispatch({ type: 'RESET' }));
  }, [transitionTo, updateSettings]);

  useKioskControls({
    phase: session.phase,
    operatorOpen,
    onOpenOperator: () => setOperatorOpen(true),
    onCloseOperator: () => setOperatorOpen(false),
    onReset: resetSession,
    onStart: startSession,
    onAnswer: selectByPosition,
  });

  usePusherControls({
    phase: session.phase,
    operatorOpen,
    onReset: resetSession,
    onStart: startSession,
    onAnswer: selectByPosition,
  });

  useEffect(() => {
    if (
      session.phase !== 'question'
      || session.selectedAnswerId === null
      || !settings.autoAdvance
    ) {
      setQuestionCountdown(0);
      return;
    }

    setQuestionCountdown(settings.feedbackDuration);
    const interval = window.setInterval(() => {
      setQuestionCountdown((current) => Math.max(0, current - 1));
    }, 1000);
    const timeout = window.setTimeout(nextQuestion, settings.feedbackDuration * 1000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [
    nextQuestion,
    session.phase,
    session.selectedAnswerId,
    settings.autoAdvance,
    settings.feedbackDuration,
  ]);

  useEffect(() => {
    if (session.phase !== 'result') return;

    setCountdown(settings.restartDuration);
    const interval = window.setInterval(() => {
      setCountdown((current) => Math.max(0, current - 1));
    }, 1000);
    const timeout = window.setTimeout(resetSession, settings.restartDuration * 1000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [resetSession, session.phase, settings.restartDuration]);

  const screenContent = useMemo(() => {
    if (session.phase === 'start') {
      return <StartView onStart={startSession} />;
    }

    if (session.phase === 'question' && currentQuestion) {
      return (
        <QuestionView
          key={currentQuestion.id}
          question={currentQuestion}
          index={session.index}
          total={session.questions.length}
          selectedAnswerId={session.selectedAnswerId}
          autoAdvance={settings.autoAdvance}
          autoAdvanceCountdown={questionCountdown}
          autoAdvanceDuration={settings.feedbackDuration}
          onSelect={selectByPosition}
          onNext={nextQuestion}
        />
      );
    }

    return (
      <ResultView
        score={session.score}
        total={session.questions.length}
        countdown={countdown}
      />
    );
  }, [
    countdown,
    currentQuestion,
    nextQuestion,
    questionCountdown,
    selectByPosition,
    session.index,
    session.phase,
    session.questions.length,
    session.score,
    session.selectedAnswerId,
    settings.autoAdvance,
    settings.feedbackDuration,
    startSession,
  ]);

  return (
    <div className="app" aria-live="polite">
      <AmbientBackground />
      <BrandHeader />

      <main className="screen">{screenContent}</main>

      <AppFooter visible={session.phase !== 'result'} />
      <LiquidTransition ref={transitionRef} />
      <OperatorPanel
        open={operatorOpen}
        settings={settings}
        questionLimit={QUESTIONS.length}
        onClose={() => setOperatorOpen(false)}
        onApply={applySettings}
        onReset={resetSession}
      />
    </div>
  );
}
